import React, { useState, useRef, useEffect } from 'react';
import {
  XMarkIcon,
  PhotoIcon,
  StarIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  Bars2Icon,
} from '@heroicons/react/24/outline';
import { useSkuPhotos, SkuPhoto } from '../../hooks/useSkuPhotos';
import { useToast } from '../../contexts/ToastContext';

interface PhotoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    sku: string;
    title: string;
    photo_url: string | null;
    vendor: string | null;
  };
  providerId: string;
  onPhotosChanged?: () => void;
}

const PhotoUploadModal: React.FC<PhotoUploadModalProps> = ({
  isOpen,
  onClose,
  product,
  providerId,
  onPhotosChanged,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [draggedPhotoId, setDraggedPhotoId] = useState<string | null>(null);
  const [dragOverPhotoId, setDragOverPhotoId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const {
    photos,
    loading,
    uploading,
    error,
    uploadPhoto,
    setPrimary,
    deletePhoto,
    reorderPhotos,
    canUploadMore,
  } = useSkuPhotos(product.sku, providerId);

  // Show toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  if (!isOpen) return null;

  // File upload drag handlers
  const handleFileDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only activate for file drops, not photo reordering
    if (draggedPhotoId) return;

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    // Ignore if this is a photo reorder drag
    if (draggedPhotoId) return;
    if (!canUploadMore) return;

    const files = Array.from(e.dataTransfer.files).filter(
      (file) =>
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/webp'
    );

    if (files.length === 0) return;

    const isPrimary = photos.length === 0;
    await uploadPhoto(files[0], isPrimary);

    for (let i = 1; i < files.length && photos.length + i < 10; i++) {
      await uploadPhoto(files[i], false);
    }

    onPhotosChanged?.();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUploadMore || !e.target.files) return;

    const files = Array.from(e.target.files);
    const isPrimary = photos.length === 0;

    for (let i = 0; i < files.length && photos.length + i < 10; i++) {
      await uploadPhoto(files[i], i === 0 && isPrimary);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onPhotosChanged?.();
  };

  const handleSetPrimary = async (photo: SkuPhoto) => {
    if (photo.is_primary) return;
    await setPrimary(photo.id);
    onPhotosChanged?.();
  };

  const handleDelete = async (photo: SkuPhoto) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    await deletePhoto(photo.id);
    onPhotosChanged?.();
  };

  // Photo reorder drag handlers
  const handlePhotoDragStart = (e: React.DragEvent, photoId: string) => {
    setDraggedPhotoId(photoId);
    e.dataTransfer.effectAllowed = 'move';
    // Add a slight delay to show the drag effect
    const target = e.currentTarget as HTMLElement;
    setTimeout(() => {
      target.style.opacity = '0.5';
    }, 0);
  };

  const handlePhotoDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedPhotoId(null);
    setDragOverPhotoId(null);
  };

  const handlePhotoDragOver = (e: React.DragEvent, photoId: string) => {
    e.preventDefault();
    if (draggedPhotoId && draggedPhotoId !== photoId) {
      setDragOverPhotoId(photoId);
    }
  };

  const handlePhotoDragLeave = () => {
    setDragOverPhotoId(null);
  };

  const handlePhotoDrop = async (e: React.DragEvent, targetPhotoId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedPhotoId || draggedPhotoId === targetPhotoId) {
      setDraggedPhotoId(null);
      setDragOverPhotoId(null);
      return;
    }

    // Reorder the photos
    const currentOrder = photos.map((p) => p.id);
    const draggedIndex = currentOrder.indexOf(draggedPhotoId);
    const targetIndex = currentOrder.indexOf(targetPhotoId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Remove dragged item and insert at new position
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedPhotoId);

    await reorderPhotos(newOrder);
    onPhotosChanged?.();

    setDraggedPhotoId(null);
    setDragOverPhotoId(null);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative modal-body rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
            <div>
              <h2 className="text-lg font-semibold text-white ">
                {product.sku}
              </h2>
              <p className="text-sm text-white/35 truncate max-w-md">
                {product.title}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/40 hover:text-white/70 rounded-full hover:bg-white/[0.08] transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Upload Zone */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-6 ${
                dragActive
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-white/15 hover:border-gray-400'
              } ${!canUploadMore ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onDragEnter={handleFileDrag}
              onDragLeave={handleFileDrag}
              onDragOver={handleFileDrag}
              onDrop={handleFileDrop}
              onClick={() => canUploadMore && fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                disabled={!canUploadMore}
              />

              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="text-sm text-white/35">Uploading...</p>
                </div>
              ) : (
                <>
                  <ArrowUpTrayIcon className="w-10 h-10 text-white/25 mx-auto mb-2" />
                  <p className="text-sm text-white/50 mb-1">
                    Drag and drop photos here, or click to browse
                  </p>
                  <p className="text-xs text-white/25">
                    JPEG, PNG, or WebP - Max 5MB each - {photos.length}/10 photos
                  </p>
                </>
              )}
            </div>

            {!canUploadMore && (
              <p className="mb-4 text-sm text-amber-600 text-center">
                Maximum 10 photos reached. Delete some to upload more.
              </p>
            )}

            {/* Drag hint */}
            {photos.length > 1 && (
              <p className="mb-4 text-xs text-white/25 text-center">
                Drag photos to reorder. First non-primary photo becomes #2 in gallery.
              </p>
            )}

            {/* Photo Grid */}
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-white/35">Loading photos...</p>
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-8 text-white/35">
                <PhotoIcon className="w-12 h-12 text-white/15 mx-auto mb-2" />
                <p>No custom photos yet</p>
                <p className="text-sm">Upload photos to override provider images</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.map((photo) => (
                  <div
                    key={photo.id}
                    draggable
                    onDragStart={(e) => handlePhotoDragStart(e, photo.id)}
                    onDragEnd={handlePhotoDragEnd}
                    onDragOver={(e) => handlePhotoDragOver(e, photo.id)}
                    onDragLeave={handlePhotoDragLeave}
                    onDrop={(e) => handlePhotoDrop(e, photo.id)}
                    className={`relative group rounded-lg overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${
                      photo.is_primary
                        ? 'border-orange-500 ring-2 ring-primary-200'
                        : 'border-white/[0.08]'
                    } ${
                      dragOverPhotoId === photo.id
                        ? 'border-primary-400 scale-105 shadow-lg'
                        : ''
                    } ${
                      draggedPhotoId === photo.id ? 'opacity-50' : ''
                    }`}
                  >
                    {/* Drag Handle */}
                    <div className="absolute top-2 right-2 z-10 p-1 bg-black bg-opacity-40 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <Bars2Icon className="w-4 h-4 text-white" />
                    </div>

                    <img
                      src={photo.photo_url}
                      alt={photo.original_filename || 'Product photo'}
                      className="w-full aspect-square object-cover pointer-events-none"
                    />

                    {/* Primary Badge */}
                    {photo.is_primary && (
                      <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-medium px-2 py-1 rounded">
                        PRIMARY
                      </div>
                    )}

                    {/* Actions Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      {!photo.is_primary && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetPrimary(photo);
                          }}
                          className="p-2 bg-white/10 rounded-full hover:bg-yellow-500/20 transition-colors"
                          title="Set as primary"
                        >
                          <StarIcon className="w-5 h-5 text-white/70" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(photo);
                        }}
                        className="p-2 bg-white/10 rounded-full hover:bg-red-500/20 transition-colors"
                        title="Delete photo"
                      >
                        <TrashIcon className="w-5 h-5 text-red-400" />
                      </button>
                    </div>

                    {/* Order Badge */}
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      #{photo.display_order + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/[0.08] bg-transparent">
            <p className="text-sm text-white/35">
              {photos.length} of 10 photos uploaded
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-900 text-white rounded  text-sm hover:bg-gray-800"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;
